import { apiUrl } from "../../../config";
import axios from "axios";

/**
 * Module de facturation d'un dossier : devis acceptés, acomptes, situations,
 * solde, avoirs et règlements (FacturationController côté API).
 *
 * Les montants sont TOUJOURS calculés par l'API : l'écran n'envoie que la
 * saisie (pourcentages, mode de reprise…) et affiche le calcul renvoyé.
 */

function entetes() {
  return {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("auth"),
  };
}

// Requête dont la réponse utile est `data` : les composants gèrent eux-mêmes
// l'affichage, le module ne garde en mémoire que la synthèse du dossier.
function requete(methode, chemin, donnees) {
  return new Promise((resolve, reject) => {
    axios({
      method: methode,
      url: apiUrl + "api/" + chemin,
      data: donnees,
      headers: entetes(),
    })
      .then((response) => {
        resolve(response.data.data);
      })
      .catch((e) => {
        console.log(e);
        reject(e);
      });
  });
}

export const state = {
  dossier: null,
  chargement: false,
};

export const mutations = {
  SET_FACTURATION_DOSSIER(state, valeur) {
    state.dossier = valeur;
  },
  SET_FACTURATION_CHARGEMENT(state, valeur) {
    state.chargement = valeur;
  },
};

export const getters = {
  FacturationDossier(state) {
    return state.dossier;
  },
  FacturationChargement(state) {
    return state.chargement;
  },
};

export const actions = {
  getFacturationDossier({ commit }, { id }) {
    commit("SET_FACTURATION_CHARGEMENT", true);
    return requete("get", "facturation/dossier/" + id)
      .then((data) => {
        commit("SET_FACTURATION_DOSSIER", data);
        commit("SET_FACTURATION_CHARGEMENT", false);
        return data;
      })
      .catch((e) => {
        commit("SET_FACTURATION_CHARGEMENT", false);
        throw e;
      });
  },

  // eslint-disable-next-line no-unused-vars
  accepterDevis({ commit }, { id, data }) {
    return requete("post", "facturation/devis/" + id + "/accepter", data);
  },

  // eslint-disable-next-line no-unused-vars
  annulerAcceptationDevis({ commit }, { id }) {
    return requete("post", "facturation/devis/" + id + "/annuler-acceptation");
  },

  // Calcul à blanc (rien n'est enregistré)
  // eslint-disable-next-line no-unused-vars
  simulerFacture({ commit }, { devisId, data }) {
    return requete("post", "facturation/devis/" + devisId + "/simuler", data);
  },

  // eslint-disable-next-line no-unused-vars
  creerBrouillonFacture({ commit }, { devisId, data }) {
    return requete("post", "facturation/devis/" + devisId + "/factures", data);
  },

  // eslint-disable-next-line no-unused-vars
  modifierBrouillonFacture({ commit }, { id, data }) {
    return requete("put", "facturation/documents/" + id, data);
  },

  // eslint-disable-next-line no-unused-vars
  supprimerBrouillonFacture({ commit }, { id }) {
    return requete("delete", "facturation/documents/" + id);
  },

  // Numéro définitif, contenu figé. Un second appel renvoie le document déjà émis.
  // eslint-disable-next-line no-unused-vars
  emettreDocumentFacturation({ commit }, { id }) {
    return requete("post", "facturation/documents/" + id + "/emettre");
  },

  // eslint-disable-next-line no-unused-vars
  getDocumentFacturation({ commit }, { id }) {
    return requete("get", "facturation/documents/" + id);
  },

  // Plateforme Agréée : nouvelle tentative après un refus ou une panne réseau.
  // eslint-disable-next-line no-unused-vars
  transmettreDocument({ commit }, { id }) {
    return requete("post", "facturation-electronique/documents/" + id + "/transmettre");
  },

  // Relit l'état chez la plateforme, sans attendre son webhook.
  // eslint-disable-next-line no-unused-vars
  etatTransmission({ commit }, { id }) {
    return requete("get", "facturation-electronique/documents/" + id + "/etat");
  },

  // eslint-disable-next-line no-unused-vars
  enregistrerReglement({ commit }, { id, data }) {
    return requete("post", "facturation/documents/" + id + "/reglements", data);
  },

  // eslint-disable-next-line no-unused-vars
  annulerReglement({ commit }, { id, motif }) {
    return requete("post", "facturation/reglements/" + id + "/annuler", { motif });
  },

  // eslint-disable-next-line no-unused-vars
  simulerAvoir({ commit }, { id, data }) {
    return requete("post", "facturation/documents/" + id + "/avoirs/simuler", data);
  },

  // eslint-disable-next-line no-unused-vars
  creerAvoir({ commit }, { id, data }) {
    return requete("post", "facturation/documents/" + id + "/avoirs", data);
  },

  // Original figé d'un document émis, aperçu « brouillon » sinon
  // eslint-disable-next-line no-unused-vars
  telechargerPdfFacturation({ commit }, { id, nom }) {
    return telecharger("facturation/documents/" + id + "/pdf", nom || "facture.pdf");
  },

  // Proforma d'un devis : informative, jamais numérotée ni enregistrée
  // eslint-disable-next-line no-unused-vars
  telechargerProforma({ commit }, { id, nom }) {
    return telecharger("facturation/devis/" + id + "/proforma", nom || "proforma.pdf");
  },
};

// Téléchargement d'un PDF de l'API. En cas d'erreur, la réponse (attendue en
// blob) est relue pour exposer le message JSON à l'écran.
function telecharger(chemin, nom) {
    return new Promise((resolve, reject) => {
      axios
        .get(apiUrl + "api/" + chemin, {
          headers: { ...entetes(), Accept: "application/pdf" },
          responseType: "blob",
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const lien = document.createElement("a");
          lien.href = window.URL.createObjectURL(blob);
          lien.download = nom;
          lien.click();
          resolve(true);
        })
        .catch((e) => {
          console.log(e);
          const data = e && e.response && e.response.data;
          if (!(data instanceof Blob)) {
            reject(e);
            return;
          }
          data
            .text()
            .then((texte) => {
              e.response.data = JSON.parse(texte);
            })
            .catch(() => {})
            .finally(() => reject(e));
        });
    });
}
